import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PRIYA } from "@/lib/images";

const PORTRAIT = PRIYA.sareeWithBook;

export default function About() {
  return (
    <div data-testid="page-about">
      {/* Header */}
      <section className="container-refined pt-16 md:pt-24 pb-14">
        <div className="max-w-3xl animate-fade-up">
          <span className="eyebrow">About the founder</span>
          <h1 className="font-serif mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Priya Kanagaraj &mdash; and the quiet reason The Life Refinery exists.
          </h1>
          <p className="font-script text-4xl mt-3">Where You&rsquo;re Heard.</p>
        </div>
      </section>

      {/* Split portrait + intro */}
      <section className="container-refined grid gap-12 md:grid-cols-[1fr_1.1fr] items-start pb-16">
        <div>
          <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border"
               style={{ borderColor: "rgba(201,168,76,0.5)", boxShadow: "0 40px 80px -40px rgba(27,67,50,0.45)" }}>
            <img src={PORTRAIT} alt="Priya, holding a book, in soft afternoon light" className="h-full w-full object-cover" />
          </div>
        </div>

        <div>
          <p className="text-base md:text-lg leading-relaxed" style={{ color: "#2A2621" }}>
            For most of my career I was the one everyone else came to for advice. Steady. Strategic.
            Successful on paper. What no one saw was how invisible I felt inside my own life &mdash;
            saying yes to rooms I didn&rsquo;t belong in and staying quiet in the ones I did.
          </p>
          <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "#2A2621" }}>
            The Life Refinery was born the year I stopped performing. When I let go of the identity
            I&rsquo;d built for approval and started listening &mdash; really listening &mdash; to
            what my own life was trying to tell me.
          </p>
          <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "#2A2621" }}>
            Today I sit with working men &mdash; the ones who look enviable from the outside and
            are secretly exhausted underneath. Together we do the slow, honest work of turning
            that ache into direction.
          </p>
          <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "#2A2621" }}>
            After years in the corporate world, I noticed the same pattern again and again: the
            men around me were the least likely to say they were struggling, and the most likely
            to be struggling. They&rsquo;d been taught that asking for help was weakness. The Life
            Refinery exists to be the one place where that rule doesn&rsquo;t apply.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
            {[
              { k: "1:1", v: "coaching container" },
              { k: "Corporate", v: "wellness sessions" },
              { k: "Warm", v: "not clinical" },
              { k: "Honest", v: "not salesy" },
            ].map((it) => (
              <div key={it.k} className="rounded-xl border p-4"
                   style={{ borderColor: "rgba(27,67,50,0.12)", background: "#FAF6EC" }}>
                <div className="font-serif text-xl" style={{ color: "#1B4332" }}>{it.k}</div>
                <div className="text-xs tracking-wider uppercase mt-1" style={{ color: "#6E6759" }}>{it.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values / philosophy */}
      <section style={{ background: "#F3ECD8" }} className="paper-grain">
        <div className="container-refined py-20 md:py-24">
          <div className="max-w-2xl">
            <span className="eyebrow">What I believe</span>
            <h2 className="font-serif mt-3 text-3xl sm:text-4xl md:text-5xl leading-tight">
              Strong doesn&rsquo;t mean silent. And asking for help isn&rsquo;t weakness.
            </h2>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              { t: "Slow is a strategy", b: "Real change is not a sprint. It's a series of small, honest decisions your future self will thank you for." },
              { t: "You are not a project", b: "You don't need optimising. You need to be heard clearly enough to remember what you actually want." },
              { t: "Purpose is quiet", b: "It rarely arrives as a lightning bolt. It arrives as a whisper you finally stop overriding." },
            ].map((v) => (
              <div key={v.t} data-testid={`value-${v.t.replace(/\s+/g, '-').toLowerCase()}`}>
                <h3 className="font-serif text-2xl md:text-3xl">{v.t}</h3>
                <p className="mt-3 text-[15px] leading-relaxed" style={{ color: "#2A2621" }}>{v.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ near the end */}
      <section className="container-refined py-16 md:py-20" data-testid="about-faq">
        <div
          className="rounded-3xl border p-8 md:p-10 max-w-3xl mx-auto"
          style={{ background: "#FAF6EC", borderColor: "rgba(201,168,76,0.5)" }}
        >
          <span className="eyebrow">A question that comes up</span>
          <h3 className="font-serif mt-3 text-2xl md:text-3xl leading-tight" style={{ color: "#1B4332" }}>
            Can a woman coach men?
          </h3>
          <p className="mt-4 text-[15px] md:text-base leading-relaxed" style={{ color: "#2A2621" }}>
            What matters here isn&rsquo;t the gender of your coach &mdash; it&rsquo;s whether
            you&rsquo;re genuinely heard, and whether things actually change. My clients come for
            practical outcomes: calmer at home, clearer at work, energy back. That&rsquo;s what we
            work toward.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="container-refined py-20 md:py-24">
        <div className="rounded-3xl p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between"
             style={{ background: "#1B4332", color: "#FAF6EC" }}>
          <div>
            <span className="eyebrow" style={{ color: "#C9A84C" }}>Ready when you are</span>
            <h3 className="font-serif text-3xl md:text-4xl mt-3 max-w-xl">
              Come as you are. Tired counts. Uncertain counts.
            </h3>
          </div>
          <Link to="/contact" data-testid="about-cta-book" className="btn-gold">
            Book a discovery call <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
